import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Mail, Linkedin, ShieldCheck, ShieldAlert, AlertTriangle, Check, X } from 'lucide-react';
import api from '../../../api/axios';
import { linkedInLogin } from '../../../redux/LIAuthSlice/liAuthSlice';
import { googleConnectUrl } from './digestHelpers';

/**
 * What the agent can actually do on the user's behalf, stated up front.
 *
 * Three rows, not two, because "signed in with Google" and "allowed to send
 * mail as you" are different facts. A token can exist with no `gmail.send`
 * scope, and a single connected/disconnected tick would show green right up
 * until the first send fails. The distinction is only useful if it is visible
 * BEFORE the user approves a hundred rows.
 */

const StatusRow = ({ icon: Icon, label, detail, state, cta, onCta, ctaBusy }) => {
    // three states: ok, warn (works, but not confirmed), bad
    const tone = {
        ok: { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', Mark: Check, text: 'Connected' },
        warn: { chip: 'bg-slate-100 text-slate-600 border-slate-300', Mark: AlertTriangle, text: 'Assumed' },
        bad: { chip: 'bg-rose-50 text-rose-700 border-rose-200', Mark: X, text: 'Not connected' },
    }[state];
    const { Mark } = tone;

    return (
        <div className="flex items-center gap-3 py-2.5">
            <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                <Icon size={15} />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-slate-900 leading-tight">{label}</p>
                {detail && <p className="text-[11.5px] text-slate-500 truncate mt-0.5">{detail}</p>}
            </div>
            {cta && (
                <button
                    type="button"
                    onClick={onCta}
                    disabled={ctaBusy}
                    className="text-[11.5px] font-semibold text-[#3F57E0] hover:underline shrink-0 disabled:opacity-60"
                >
                    {ctaBusy ? 'Connecting…' : cta}
                </button>
            )}
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-semibold border shrink-0 ${tone.chip}`}>
                <Mark size={10} strokeWidth={3} /> {tone.text}
            </span>
        </div>
    );
};

/**
 * Each CTA starts its provider's consent flow FROM HERE.
 *
 * It deliberately does not route through /profile with a `?connectGoogle=1`
 * flag. That worked, but the user watched a whole page load and repaint before
 * the consent screen appeared — a visible detour through a page they did not
 * ask for and have no reason to be on. The profile page adds nothing to the
 * decision: it reads the flag and immediately redirects. So the redirect is
 * issued directly and the intermediate render never happens.
 *
 * Google and Gmail are ONE grant, not two. `gmail.send` is a scope on the same
 * Google account, so both rows run the identical flow; the backend forces
 * `prompt: 'consent'` and records the scopes actually granted, which is what
 * resolves the "assumed" state.
 *
 * LinkedIn is a different shape: `linkedInLogin` fetches a Unipile auth URL,
 * and Navbaar's global `linkedInAuthUrl` watcher opens the popup. Dispatching
 * is the whole job — opening a window here would race that watcher and produce
 * two popups.
 */
const ConnectionStatus = ({ channels }) => {
    const dispatch = useDispatch();
    const userEmail = useSelector((s) => s.auth.user?.email);
    const liConnecting = useSelector((s) => s.liauth?.isLoading);

    // Built by the shared helper so this strip and the blocked send button in
    // the detail pane cannot drift apart.
    const connectGoogle = () => {
        const url = googleConnectUrl({
            baseURL: api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin,
            userEmail,
            origin: window.location.origin
        });
        if (url) window.open(url, '_self');
    };

    const connectLinkedIn = () => dispatch(linkedInLogin());

    if (!channels) return null;

    const { google = {}, gmail = {}, linkedin = {} } = channels;

    const gmailState = !gmail.canSend ? 'bad' : gmail.assumed ? 'warn' : 'ok';
    const gmailDetail = !gmail.canSend
        ? (gmail.reason === 'gmail_no_send_scope'
            ? 'Signed in, but send permission was not granted.'
            : 'Drafts can still be copied and sent by hand.')
        : gmail.assumed
            ? 'Granted before we tracked permissions — reconnect to confirm.'
            : 'The agent can send as you.';

    const linkedinDetail = linkedin.connected
        ? 'Invites and messages can be sent as you.'
        : linkedin.reason === 'linkedin_rate_limited'
            ? 'Paused until tomorrow to protect the account.'
            : 'Drafts can still be copied and sent by hand.';

    return (
        <div className="divide-y divide-slate-100">
            <StatusRow
                icon={ShieldCheck}
                label="Google account"
                detail={google.connected ? (google.email || 'Connected') : 'Not signed in with Google.'}
                state={google.connected ? 'ok' : 'bad'}
                cta={google.connected ? null : 'Connect'}
                onCta={connectGoogle}
            />
            <StatusRow
                icon={Mail}
                label="Gmail sending"
                detail={gmailDetail}
                state={gmailState}
                cta={gmailState === 'ok' ? null : 'Reconnect'}
                onCta={connectGoogle}
            />
            <StatusRow
                icon={linkedin.connected ? Linkedin : ShieldAlert}
                label="LinkedIn"
                detail={linkedinDetail}
                state={linkedin.connected ? 'ok' : linkedin.reason === 'linkedin_rate_limited' ? 'warn' : 'bad'}
                cta={linkedin.connected || linkedin.reason === 'linkedin_rate_limited' ? null : 'Connect'}
                onCta={connectLinkedIn}
                ctaBusy={liConnecting}
            />
        </div>
    );
};

export default ConnectionStatus;
